import React from 'react';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { animate, motion, transform, useMotionValue, useTransform } from 'framer-motion';
import useOffsetSize from 'hooks/useOffsetSize';
import { useIsomorphicLayoutEffect } from 'react-use';

/**
 * @typedef {import("@prismicio/client").Content.LogosAutoScrollSlice} LogosAutoScrollSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<LogosAutoScrollSlice>} LogosAutoScrollProps
 * @param { LogosAutoScrollProps }
 */

const transition = { type: 'tween', repeat: Infinity, duration: 25, ease: 'linear' };

const LoopClonesComponent = ({ className, playing, children, direction = 1 }) => {
  const [{ width: cw }, $container] = useOffsetSize();
  const [{ width: sw }, $source] = useOffsetSize();
  const [nbClones, setNbClones] = useState(null);

  const t = useMotionValue(0);
  const x = useTransform(t, [0, 100], direction > 0 ? [-sw, 0] : [0, -sw]);

  useIsomorphicLayoutEffect(() => {
    if (cw && sw) {
      const nb = Math.ceil(cw / sw);
      setNbClones(nb);
    }
  }, [sw, cw]);

  useEffect(() => {
    if (playing) {
      t.set(0);
      const duration = transform(sw, [0, 1300], [0, 25], { clamp: false });
      const control = animate(t, 100, { ...transition, duration });
      return () => control.stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, nbClones]);

  return (
    <Scroll ref={$container} className={className}>
      <Block ref={$source} style={{ x }}>
        {children}
      </Block>
      {nbClones !== null &&
        Array.from({ length: nbClones }).map((_, index) => (
          <Block key={index} style={{ x }}>
            {children}
          </Block>
        ))}
    </Scroll>
  );
};

const Scroll = styled.div`
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  overflow: hidden;
`;

export const Block = styled(motion.div)`
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
`;

export const LoopClones = styled(LoopClonesComponent)`
  text-align: center;
`;
